import {
  Alert,
  Button,
  Col,
  Descriptions,
  Form,
  List,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
} from "antd";
import { DeleteOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import {
  WellbeingReport,
  WellbeingReportStatus,
  Reviewer,
  ReportFile,
  ManagerUser,
  EmployeeUser,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import {
  getWellbeingReport,
  getReportReviewers,
  addReportReviewer,
  deleteReportReviewers,
  getWellbeingReportFiles,
  addReportFile,
  deleteReportFile,
  previewReportFile,
  updateWellbeingReport,
  submitReport,
  reopenReport,
  getCompanies,
  getCompanyManagers,
  getCompanyEmployees,
} from "../../repository/data.repository";
import {
  reportStatusColor,
  reportGuidance,
  reportVerdictColor,
} from "./ReportsView.default";
import { retry } from "../../utils/http";
import { fmtDateTime as fmt } from "../../utils/date";
import { nameOf } from "../../utils/typescript";
import { QueryOperation } from "../../code/search-query/search-query";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import InModal from "../../controls/InModal";
import DynamicForm from "../../controls/DynamicForm";
import ManageStatusBanner from "../../controls/ManageStatusBanner";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface ReportManageDialogProps {
  readonly open?: boolean;
  readonly record?: WellbeingReport;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

/**
 * Unified Manage surface for a wellbeing report (mirrors the counselling Manage dialog). Details, reviewers and
 * files are editable only while Draft; once Submitted/Appraised it is read-only and shows the reviewers' verdicts.
 * Author/admin can Submit a draft; an admin can Reopen a submitted/appraised report to fix a stuck case.
 */
function ReportManageDialog(props: ReportManageDialogProps) {
  const isAdmin = useSelector(authorizeSelectors.isAppAdmin);

  const [detailsForm] = Form.useForm();
  const [reviewerForm] = Form.useForm();

  const [report, setReport] = useState<WellbeingReport | undefined>(props.record);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [files, setFiles] = useState<ReportFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const status = report?.status;
  const isDraft = status === WellbeingReportStatus.Draft;
  const canSubmit = isDraft && reviewers.length > 0 && files.length > 0;

  useEffect(() => {
    if (props.open && props.record) {
      setReport(props.record);
      setError(false);
      detailsForm.setFieldsValue({
        company: { display: props.record.companyName, value: props.record.companyUid },
        user: {
          display: props.record.reportingForUserUserName,
          value: props.record.reportingForUserUid,
        },
        summary: props.record.summary,
      });
      reviewerForm.resetFields();
      loadSubResources(props.record.uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // Methods

  function loadSubResources(uid: string): void {
    setLoading(true);
    Promise.all([
      retry(() =>
        getReportReviewers(
          { pageNumber: 0, pageSize: MAX_PAGE_SIZE, searchQuery: { conditions: [] } },
          uid,
        ),
      ),
      retry(() =>
        getWellbeingReportFiles(
          { pageNumber: 0, pageSize: MAX_PAGE_SIZE, searchQuery: { conditions: [] } },
          uid,
        ),
      ),
    ])
      .then(([rev, fls]) => {
        setReviewers(rev.data);
        setFiles(fls.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  function reloadReport(): void {
    if (!report) return;
    getWellbeingReport(report.uid).then((fresh) => {
      if (fresh) setReport(fresh);
    });
  }

  function run(action: () => Promise<unknown>): void {
    setLoading(true);
    setError(false);
    action()
      .then(() => setLoading(false))
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  // Reviewer + file actions apply immediately (they don't touch the report's RowVersion).

  function onAddReviewer(): void {
    const reviewer = reviewerForm.getFieldValue("reviewer");
    if (!reviewer?.value || !report) return;
    run(() =>
      addReportReviewer(report.uid, reviewer.value).then(() => {
        reviewerForm.resetFields();
        loadSubResources(report.uid);
      }),
    );
  }

  function onRemoveReviewer(userUid: string): void {
    if (!report) return;
    run(() => deleteReportReviewers(report.uid, userUid).then(() => loadSubResources(report.uid)));
  }

  function onUploadFile(file: File): boolean {
    if (report) run(() => addReportFile(report.uid, file).then(() => loadSubResources(report.uid)));
    return false; // prevent antd's default upload
  }

  function onRemoveFile(fileUid: string): void {
    if (!report) return;
    run(() => deleteReportFile(fileUid).then(() => loadSubResources(report.uid)));
  }

  function onSaveDetails(): void {
    if (!report) return;
    const { company, user, summary } = detailsForm.getFieldsValue();
    run(() =>
      updateWellbeingReport({
        uid: report.uid,
        rowVersion: report.rowVersion,
        summary,
        companyUid: company?.value,
        reportingForUserUid: user?.value,
      }).then(() => reloadReport()),
    );
  }

  function onSubmit(): void {
    if (!report) return;
    run(() => submitReport(report.uid).then(() => props.onFinish?.()));
  }

  function onReopen(): void {
    if (!report) return;
    run(() => reopenReport(report.uid).then(() => props.onFinish?.()));
  }

  const companiesRequest = createSearchRequest(getCompanies, "name");

  // Subject follows the company currently picked in the Details form (it's saved together with
  // it), falling back to the persisted company before any edit — so changing company in Draft
  // re-scopes the subject list. The `user` control's `dependsOn: ["company"]` clears a stale one.
  const usersRequest = (search: string) => {
    const companyUid = detailsForm.getFieldValue("company")?.value ?? report?.companyUid;
    const conditions: any[] = [
      { field: nameOf<EmployeeUser>("firstName"), operation: QueryOperation.StartsWith, values: [search] },
    ];
    if (companyUid)
      conditions.push({ field: nameOf<EmployeeUser>("companyUid"), operation: QueryOperation.Equal, values: [companyUid] });

    return retry(() => getCompanyEmployees({ searchQuery: { conditions }, pageNumber: 0, pageSize: 100 })).then(({ data }) =>
      data.map((d) => ({ display: d.userName, subDisplay: d.companyName, value: d.uid })),
    );
  };

  // Reviewers must be managers of the report's company (the backend enforces this too).
  const managersRequest = (search: string) => {
    const conditions: any[] = [
      { field: nameOf<ManagerUser>("firstName"), operation: QueryOperation.StartsWith, values: [search] },
    ];
    if (report?.companyUid)
      conditions.push({ field: nameOf<ManagerUser>("companyUid"), operation: QueryOperation.Equal, values: [report.companyUid] });

    return retry(() => getCompanyManagers({ searchQuery: { conditions }, pageNumber: 0, pageSize: 100 })).then(({ data }) =>
      data.map((d) => ({ display: d.userName, subDisplay: d.companyName, value: d.uid })),
    );
  };

  return (
    <InModal
      open={props.open}
      size="large"
      title="Manage Wellbeing Report"
      onCancel={props.onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={props.onCancel}>
            Close
          </Button>
          {isDraft && (
            <Button loading={loading} onClick={onSaveDetails}>
              Save Details
            </Button>
          )}
          {isDraft && (
            <Button type="primary" loading={loading} disabled={!canSubmit} onClick={onSubmit}>
              Submit for Review
            </Button>
          )}
          {isAdmin && !isDraft && (
            <Button loading={loading} onClick={onReopen}>
              Reopen to Draft
            </Button>
          )}
        </Space>
      }
    >
      {/* Status banner + guidance */}
      <ManageStatusBanner
        tagColor={reportStatusColor(status)}
        status={status}
        guidance={reportGuidance(status)}
        extra={
          report?.createdByUserUserName && (
            <Typography.Text type="secondary" className="whitespace-nowrap text-xs">
              Author: {report.createdByUserUserName}
            </Typography.Text>
          )
        }
      />

      <Row gutter={16} className="flex flex-row">
        {/* Details — editable while Draft */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Details
          </Typography.Text>
          {isDraft ? (
            <DynamicForm
              form={detailsForm}
              schema={{
                groups: [
                  {
                    controls: [
                      { type: "combobox", name: "company", label: "Company", request: companiesRequest },
                      { type: "combobox", name: "user", label: "Subject", request: usersRequest, dependsOn: ["company"] },
                      { type: "areabox", name: "summary", label: "Summary", rows: 5 },
                    ],
                  },
                ],
              }}
            />
          ) : (
            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                { key: "company", label: "Company", children: report?.companyName ?? "—" },
                { key: "subject", label: "Subject", children: report?.reportingForUserUserName ?? "—" },
                { key: "summary", label: "Summary", children: report?.summary || "—" },
                { key: "submittedOn", label: "Submitted", children: fmt(report?.submittedOn) },
              ]}
            />
          )}
        </Col>

        {/* Reviewers — with verdicts; add/remove while Draft */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Reviewers
          </Typography.Text>
          {isDraft && (
            <DynamicForm
              form={reviewerForm}
              schema={{
                groups: [
                  {
                    controls: [
                      { type: "combobox", name: "reviewer", label: "Add reviewer (company manager)", request: managersRequest },
                    ],
                  },
                ],
              }}
              onChange={onAddReviewer}
            />
          )}
          <List
            size="small"
            dataSource={reviewers}
            locale={{ emptyText: "No reviewers yet" }}
            renderItem={(rev) => (
              <List.Item
                className="px-1"
                actions={
                  isDraft
                    ? [
                        <Button
                          key="remove"
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => onRemoveReviewer(rev.userUid)}
                        />,
                      ]
                    : undefined
                }
              >
                <Col className="flex flex-auto flex-col leading-tight">
                  <span>
                    {rev.userUserName}{" "}
                    <Tag color={reportVerdictColor(rev.verdict)} className="ml-1">
                      {rev.verdict}
                    </Tag>
                  </span>
                  {rev.notes && (
                    <Typography.Text type="secondary" className="text-xs">
                      {rev.notes}
                    </Typography.Text>
                  )}
                  {rev.appraisedOn && (
                    <Typography.Text type="secondary" className="text-xs">
                      {fmt(rev.appraisedOn)}
                    </Typography.Text>
                  )}
                </Col>
              </List.Item>
            )}
          />
        </Col>

        {/* Files — upload while Draft; preview always */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Files (PDF)
          </Typography.Text>
          {isDraft && (
            <Upload accept=".pdf" showUploadList={false} beforeUpload={(file) => onUploadFile(file as File)}>
              <Button icon={<UploadOutlined />} className="mb-2">
                Upload PDF
              </Button>
            </Upload>
          )}
          <List
            size="small"
            dataSource={files}
            locale={{ emptyText: "No files yet" }}
            renderItem={(file) => (
              <List.Item
                className="px-1"
                actions={[
                  <Button key="preview" type="text" icon={<EyeOutlined />} onClick={() => previewReportFile(file.uid)} />,
                  ...(isDraft
                    ? [<Button key="remove" type="text" icon={<DeleteOutlined />} onClick={() => onRemoveFile(file.uid)} />]
                    : []),
                ]}
              >
                <Typography.Text className="truncate">
                  {file.fileName}
                  {file.fileExtension}
                </Typography.Text>
              </List.Item>
            )}
          />
        </Col>
      </Row>

      {error && <Alert className="mt-3" message="Something went wrong, try again" type="error" />}
    </InModal>
  );
}

export default ReportManageDialog;
